import { ElementsSDK } from '@yiluhub/ui-sdk-react';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { paramConverters } from 'utils';
import {
  isPaymentInformationPage,
  isProductDetailsPage,
  isSearchResultsPage,
  isTravellerInformationPage,
} from 'utils/headerUtils';
import { getProductDetailPageQueryParams, getProductFromUrl } from 'utils/paramConverters';

import './styles.scss';

type ProgressBarProps = {
  detailsStepLabel?: string;
  personalInfoStepLabel?: string;
  paymentStepLabel?: string;
  srpOnClick?: () => void;
};

function useProgressClickEvents(product: string) {
  const navigate = useNavigate();
  const location = useLocation();

  const srpOnClick = () => {
    navigate({
      pathname: `/${product}/results`,
    });
  };

  const pdpOnClick = () => {
    navigate({
      pathname: `/${product}/product`,
      search: paramConverters.getURLSearchQuery(getProductDetailPageQueryParams(location)),
    });
  };

  const tipOnClick = () => {
    navigate({
      pathname: `/${product}/booking/passengers`,
      search: paramConverters.getURLSearchQuery(getProductDetailPageQueryParams(location)),
    });
  };
  return { pdpOnClick, tipOnClick, srpOnClick };
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  detailsStepLabel,
  personalInfoStepLabel,
  paymentStepLabel,
  srpOnClick,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const product = getProductFromUrl(pathname)!;

  const detailsStep = detailsStepLabel || (t('header.progressStep.detail') as string);
  const personalInfoStep =
    personalInfoStepLabel || (t('header.progressStep.personalInfo') as string);
  const paymentStep = paymentStepLabel || (t('header.progressStep.payment') as string);
  const { pdpOnClick, tipOnClick } = useProgressClickEvents(product);

  const getSRPStepState = () => {
    return isSearchResultsPage(pathname)
      ? ElementsSDK.StepStates.current
      : ElementsSDK.StepStates.active;
  };

  const getPDPStepState = () => {
    return isProductDetailsPage(pathname)
      ? ElementsSDK.StepStates.current
      : ElementsSDK.StepStates.active;
  };

  const getPIPStepState = () => {
    if (isTravellerInformationPage(pathname)) {
      return ElementsSDK.StepStates.current;
    } else {
      return isProductDetailsPage(pathname)
        ? ElementsSDK.StepStates.inactive
        : ElementsSDK.StepStates.active;
    }
  };

  return (
    <div
      className={clsx('header-progressBar', {
        'long-first': t('header.progressStep.detail')?.length > 7,
      })}
    >
      <ElementsSDK.Progress>
        <ElementsSDK.ProgressStep
          stepState={srpOnClick ? getSRPStepState() : getPDPStepState()}
          title={srpOnClick ? t('header.progressStep.results') : detailsStep}
          index={1}
          as="a"
          onClick={srpOnClick ?? pdpOnClick}
        />
        <ElementsSDK.ProgressStep
          stepState={getPIPStepState()}
          title={personalInfoStep}
          index={2}
          as="a"
          onClick={tipOnClick}
        />
        <ElementsSDK.ProgressStep
          stepState={
            isPaymentInformationPage(pathname)
              ? ElementsSDK.StepStates.current
              : ElementsSDK.StepStates.inactive
          }
          title={paymentStep}
          index={3}
        />
        <ElementsSDK.ProgressStep
          stepState={ElementsSDK.StepStates.inactive}
          title={t('header.progressStep.confirmation')}
          isCheck
        />
      </ElementsSDK.Progress>
    </div>
  );
};

export default ProgressBar;
